import { ValidationError } from '@tanstack/react-form'
import { LucideTriangleAlert } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { type ReactNode, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CampaignLanguageSelectField } from '../../constructs'
import {
	AnimateChangeInHeight,
	LargeTextField,
	TextField,
	WidgetInputTextField,
	WidgetLabel,
	WidgetSelectGroup,
} from '../../rapid-ui'

type DetailsAreaProps = {
	title: string
	location: string
	language: string
	description: string
	url: string

	onTitleChange: (title: string) => void
	onLocationChange: (location: string) => void
	onLanguageChange: (language: string) => void
	onDescriptionChange: (description: string) => void
	onUrlChange: (url: string) => void

	titleErrors: Array<ValidationError>
	locationErrors: Array<ValidationError>
	languageErrors: Array<ValidationError>
	descriptionErrors: Array<ValidationError>
	urlErrors: Array<ValidationError>

	mode: string
	onModeChange: (mode: string) => void

	isReady: boolean
	scrapeError?: string
	/** Offers "Import from ATS" as a third way to fill in the details. */
	atsJobPicker?: ReactNode
}

export function DetailsArea({ mode, onModeChange, isReady, scrapeError, atsJobPicker, ...props }: DetailsAreaProps) {
	const { t } = useTranslation('common')

	useEffect(() => {
		if (scrapeError) {
			onModeChange('manual')
		}
	}, [scrapeError, onModeChange])

	const modes = [
		{
			seq: 0,
			id: 'scan',
			label: t('editor.details.scan'),
			render: () => <div>{t('editor.details.scan')}</div>,
		},
		{
			seq: 1,
			id: 'manual',
			label: t('editor.details.manual'),
			render: () => <div>{t('editor.details.manual')}</div>,
		},
		...(atsJobPicker
			? [{
				seq: 2,
				id: 'ats',
				label: t('editor.details.fromAtsJob'),
				render: () => <div>{t('editor.details.fromAtsJob')}</div>,
			}]
			: []),
	]

	if (!isReady) {
		return <div>{t('editor.details.loading')}</div>
	}

	return (
		<div className="flex flex-col gap-2 mt-8">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="text-lg font-medium">
					{t('editor.details.jobDetails')}
				</div>
				<div>
					<WidgetSelectGroup
						value={mode}
						onChange={onModeChange}
						items={modes}
					/>
				</div>
			</div>
			{scrapeError && (
				<div
					role="alert"
					className="flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 text-sm text-amber-800"
				>
					<LucideTriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-500" />
					<div>
						<p className="font-medium">{t('editor.details.readErrorTitle')}</p>
						<p className="mt-0.5 text-xs text-amber-700">
							{scrapeError}
						</p>
					</div>
				</div>
			)}
			<div className="bg-grey-50 rounded-lg overflow-hidden relative">
				<AnimateChangeInHeight duration={0.25}>
					<AnimatePresence mode="popLayout" initial={false}>
						<motion.div
							layoutId={`details-area-${mode}`}
							key={mode}
							className="bg-grey-50"
							initial={{
								opacity: 0,
								filter: 'blur(2px)',
							}}
							animate={{
								opacity: 1,
								translateX: 0,
								filter: 'blur(0px)',
							}}
							exit={{
								opacity: 0,
								filter: 'blur(2px)',
							}}
							transition={{ duration: 0.4, type: 'spring', bounce: 0 }}
						>
							{mode === 'scan' && (
								<JobDetailsWithScan
									value={props.url}
									onChange={props.onUrlChange}
									errors={props.urlErrors}
								/>
							)}
							{mode === 'ats' && atsJobPicker}
							{mode === 'manual' && (
								<JobDetailsWithManual
									title={props.title}
									location={props.location}
									language={props.language}
									description={props.description}
									onTitleChange={props.onTitleChange}
									onLocationChange={props.onLocationChange}
									onLanguageChange={props.onLanguageChange}
									onDescriptionChange={props.onDescriptionChange}
									titleErrors={props.titleErrors}
									locationErrors={props.locationErrors}
									languageErrors={props.languageErrors}
									descriptionErrors={props.descriptionErrors}
								/>
							)}
						</motion.div>
					</AnimatePresence>
				</AnimateChangeInHeight>
			</div>
		</div>
	)
}

function JobDetailsWithScan({
	value,
	onChange,
	errors,
}: {
	value: string
	onChange: (v: string) => void
	errors: Array<ValidationError>
}) {
	const { t } = useTranslation('common')
	return (
		<div className="flex flex-col gap-4 p-4 ">
			<WidgetLabel label={t('editor.details.jobPostingUrl')} htmlFor="job-posting-url">
				<WidgetInputTextField
					id="job-posting-url"
					value={value}
					onChange={(e) => onChange(e)}
					placeholder="https://example.com"
					hint={t('editor.details.scanHint')}
					errors={errors}
				/>
			</WidgetLabel>
		</div>
	)
}

function JobDetailsWithManual({
	title,
	location,
	language,
	description,
	onTitleChange,
	onLocationChange,
	onLanguageChange,
	onDescriptionChange,
	titleErrors,
	locationErrors,
	languageErrors,
	descriptionErrors,
}: {
	title: string
	location: string
	language: string
	description: string
	onTitleChange: (title: string) => void
	onLocationChange: (location: string) => void
	onLanguageChange: (language: string) => void
	onDescriptionChange: (description: string) => void

	titleErrors: Array<ValidationError>
	locationErrors: Array<ValidationError>
	languageErrors: Array<ValidationError>
	descriptionErrors: Array<ValidationError>
}) {
	const { t } = useTranslation('common')
	const titlePlaceholder = useMemo(() => {
		const placeholders = [
			'Senior Software Engineer',
			'Marketing Manager',
			'Product Designer',
			'Data Scientist',
			'Customer Success Specialist',
			'DevOps Engineer',
			'UX Researcher',
			'Electrician',
			'Financial Analyst',
			'Nurse Practitioner',
			'HR Coordinator',
		]
		return placeholders[Math.floor(Math.random() * placeholders.length)]
	}, [])

	return (
		<div className="flex flex-col gap-4 p-4  @container">
			<TextField
				sizing="small"
				label={t('editor.details.title')}
				onChange={(e) => onTitleChange(e)}
				value={title}
				placeholder={titlePlaceholder}
				errors={titleErrors}
			/>
			<div className="grid grid-cols-1 @sm:grid-cols-2 gap-4">
				<TextField
					sizing="small"
					label={t('editor.details.location')}
					onChange={(e) => onLocationChange(e)}
					value={location}
					placeholder={t('editor.details.locationPlaceholder')}
					errors={locationErrors}
				/>

				<CampaignLanguageSelectField
					sizing="small"
					value={language}
					onChange={(e) => onLanguageChange(e)}
					errors={languageErrors}
				/>
			</div>
			<LargeTextField
				sizing="small"
				label={t('editor.details.description')}
				onChange={(e) => onDescriptionChange(e)}
				value={description}
				hint={t('editor.details.descriptionHint')}
				errors={descriptionErrors}
			/>
		</div>
	)
}
